import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ChevronLeft, FileText, Image as ImageIcon, Check } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CASE_STATUSES } from "@/components/StatusBadge";
import { DatePicker } from "@/components/DatePicker";
import { BottomNav } from "@/components/BottomNav";

const CATEGORIES = ["FINANZAMT", "BUERGERAMT", "AUSLAENDERBEHOERDE", "JOBCENTER", "KRANKENKASSE", "OTHER"];

export default function CaseNew() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("OTHER");
  const [catSuggested, setCatSuggested] = useState(false);
  const [status, setStatus] = useState("NEW");
  const [reference, setReference] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [notes, setNotes] = useState("");
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);
  const [params] = useSearchParams();
  const fromDoc = params.get("from");
  const [prefilled, setPrefilled] = useState(false);

  useEffect(() => {
    if (!fromDoc) return;
    api.get(`/documents/${fromDoc}/case-prefill`).then(({ data }) => {
      if (data.title) setTitle(data.title);
      if (data.reference_number) setReference(data.reference_number);
      if (data.deadline) setDueDate(data.deadline);
      // AI-proposed category from the sender; user can still override it below.
      if (data.category && data.category !== "OTHER") {
        setCategory(data.category);
        setCatSuggested(true);
      }
      setSelected((s) => (s.includes(fromDoc) ? s : [...s, fromDoc]));
      setPrefilled(true);
    }).catch(() => {});
  }, [fromDoc]);

  const { data: docs = [] } = useQuery({
    queryKey: ["documents"],
    queryFn: async () => (await api.get("/documents")).data,
  });

  const toggleDoc = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/cases", {
        title: title.trim(),
        category,
        status,
        reference_number: reference.trim() || null,
        due_date: dueDate || null,
        notes: notes.trim() || null,
        document_ids: selected,
      });
      toast.success(t("caseForm.created"));
      qc.invalidateQueries({ queryKey: ["cases"] });
      navigate(`/cases/${data.id}`, { replace: true });
    } catch (err) {
      toast.error(apiError(err, t));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="relative mx-auto min-h-screen max-w-md border-x border-[#E2EAE5]/60 bg-[#F8FAF8] pb-28 shadow-sm">
      <header className="sticky top-0 z-40 flex items-center gap-2 border-b border-[#E2EAE5] bg-[#F8FAF8]/90 px-4 py-3 backdrop-blur-md">
        <button data-testid="case-back-button" onClick={() => navigate(-1)} className="rounded-full p-1.5 text-[#4A6358] hover:bg-[#F0F4F1]">
          <ChevronLeft size={22} />
        </button>
        <h1 className="font-display text-base font-bold text-[#1A2E26]">{t("caseForm.title")}</h1>
      </header>

      <form onSubmit={submit} className="esv-fade space-y-5 px-4 pt-5">
        {prefilled && (
          <p data-testid="case-prefill-note" className="rounded-xl border border-[#C4DFD2] bg-[#F2F7F4] px-3 py-2 text-xs text-[#2D5A4C]">
            {t("e3.prefill")}
          </p>
        )}
        <div>
          <label className="mb-1.5 block text-xs font-semibold text-[#4A6358]">{t("caseForm.titleLabel")}</label>
          <Input
            data-testid="case-title-input"
            className="h-12 rounded-xl"
            placeholder={t("caseForm.titlePlaceholder")}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-[#4A6358]">
              {t("caseForm.category")}
              {catSuggested && (
                <span data-testid="category-ai-badge" className="rounded-full bg-[#EDF7F2] px-1.5 py-0.5 text-[9px] font-bold text-[#1B6943]">
                  {t("e4.aiSuggest")}
                </span>
              )}
            </label>
            <Select value={category} onValueChange={(v) => { setCategory(v); setCatSuggested(false); }}>
              <SelectTrigger data-testid="case-category-select" className="h-12 rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c}>{t(`categories.${c}`)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-[#4A6358]">{t("caseForm.status")}</label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="case-status-select" className="h-12 rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CASE_STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>{t(`status.${s}`)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-[#4A6358]">
              {t("caseForm.reference")} <span className="text-[#B0C2B8]">({t("common.optional")})</span>
            </label>
            <Input
              data-testid="case-reference-input"
              className="h-12 rounded-xl font-mono text-sm"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-[#4A6358]">
              {t("caseForm.dueDate")} <span className="text-[#B0C2B8]">({t("common.optional")})</span>
            </label>
            <DatePicker value={dueDate} onChange={setDueDate} testId="case-duedate-input" />
          </div>
        </div>

        <div>
          <label className="mb-1.5 block text-xs font-semibold text-[#4A6358]">
            {t("caseForm.notes")} <span className="text-[#B0C2B8]">({t("common.optional")})</span>
          </label>
          <Textarea
            data-testid="case-notes-input"
            className="min-h-[80px] rounded-xl"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>

        <div>
          <label className="mb-1.5 block text-xs font-semibold text-[#4A6358]">{t("caseForm.attachDocs")}</label>
          {docs.length === 0 ? (
            <p className="rounded-xl border border-[#E2EAE5] bg-white/60 px-3 py-3 text-xs text-[#B0C2B8]">
              {t("caseForm.noDocsYet")}
            </p>
          ) : (
            <div className="space-y-2">
              {docs.map((d) => {
                const isPdf = d.content_type === "application/pdf";
                const active = selected.includes(d.id);
                return (
                  <button
                    key={d.id}
                    type="button"
                    data-testid={`case-attach-doc-${d.id}`}
                    onClick={() => toggleDoc(d.id)}
                    className={`flex w-full items-center gap-3 rounded-2xl border p-3 text-left transition-all ${active ? "border-[#2D5A4C] bg-[#F2F7F4]" : "border-[#E2EAE5] bg-white"}`}
                  >
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#F0F4F1] text-[#2D5A4C]">
                      {isPdf ? <FileText size={16} /> : <ImageIcon size={16} />}
                    </div>
                    <span className="min-w-0 flex-1 truncate text-sm font-medium text-[#1A2E26]">{d.original_filename}</span>
                    <span className={`flex h-5 w-5 items-center justify-center rounded-full border ${active ? "border-[#2D5A4C] bg-[#2D5A4C] text-white" : "border-[#C4DFD2]"}`}>
                      {active && <Check size={13} />}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <Button
          data-testid="case-create-submit"
          type="submit"
          disabled={saving}
          className="h-12 w-full rounded-xl bg-[#2D5A4C] text-base font-semibold hover:bg-[#214439]"
        >
          {saving ? t("common.saving") : t("caseForm.create")}
        </Button>
      </form>
      <BottomNav />
    </div>
  );
}
